import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, MenuItem, TextField, IconButton, Box, Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Lock as LockIcon } from '@mui/icons-material';
import { PowerAddonLineItem, NormalizedPowerDefaultAddon, SelectOption, HEADER_CELL_SX, TABLE_CELL_SX, SPACER_CELL_SX, rowErrorSX, NO_SPIN_SX, CELL_W, COL } from './types';
import { PowerAddon, PowerAddonOption, PowerAddonOptionPricingRow } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';

interface PowerAddonsTableProps {
  powerAddonItems: PowerAddonLineItem[];
  powerAddons: PowerAddon[];
  powerAddonOptions: Record<number, PowerAddonOption[]>;
  powerAddonOptionPricing: Record<string, PowerAddonOptionPricingRow>;
  powerDefaultAddons: NormalizedPowerDefaultAddon[];
  selectedRegion: number | string;
  pricingReady: boolean;
  onUpdatePowerAddon: (id: number, field: string, value: unknown) => void;
  onDeletePowerAddon: (id: number) => void;
  isPowerAddonRowAvailable: (item: PowerAddonLineItem) => boolean;
  getPowerAddonCostByIds: (powerAddonId: number, optionId: number | null) => number;
  ensureCurrentOption: (options: SelectOption[], currentValue: number | string | null | undefined, makeLabel: (v: number | string) => string) => SelectOption[];
}

const PowerAddonsTable: React.FC<PowerAddonsTableProps> = ({
  powerAddonItems, powerAddons, powerAddonOptions, powerAddonOptionPricing,
  powerDefaultAddons, selectedRegion, pricingReady,
  onUpdatePowerAddon, onDeletePowerAddon,
  isPowerAddonRowAvailable, getPowerAddonCostByIds, ensureCurrentOption,
}) => {
  if (powerAddonItems.length === 0) return null;

  // One-time addons are auto-managed — never appear in manual selection dropdowns

  return (
    <Paper sx={{ mb: 3, elevation: 2, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ p: 1, backgroundColor: 'grey.300', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>IBM Power Addons</Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.NAME) }}>Power Addon</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.OS2) }}>Option</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...SPACER_CELL_SX }}></TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>Qty</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right' }}>Cost</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ACT) }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {powerAddonItems.map((item) => {
              const available = isPowerAddonRowAvailable(item);
              const addonId = parseInt(String(item.powerAddonId), 10);
              const optionId = item.powerAddonOptionId ? parseInt(String(item.powerAddonOptionId), 10) : null;
              const unitCost = getPowerAddonCostByIds(addonId, optionId);
              const cost = item.savedLineCost != null ? item.savedLineCost : unitCost * item.quantity;
              const isOneTime = !!powerAddons.find((a) => a.PowerAddonId === addonId)?.IsOneTime;

              // One-time addons are auto-managed; only recurring addons appear in the manual dropdown
              const addonOptionsList: SelectOption[] = powerAddons
                .filter(a => !a.IsOneTime || a.PowerAddonId === addonId)
                .map((a) => ({ value: a.PowerAddonId, label: a.PowerAddonName }));
              const addonLabel = (val: number | string) => powerAddons.find((a) => a.PowerAddonId === Number(val))?.PowerAddonName ?? String(val);
              const addonOptsWithCurrent = ensureCurrentOption(addonOptionsList, item.powerAddonId, addonLabel);
              const includedEnvs = addonId
                ? powerDefaultAddons.filter((da) => da.PowerAddonId === addonId).map((da) => da.EnvironmentCode).filter(Boolean)
                : [];
              const includedEnvsLabel = includedEnvs.length > 1
                ? includedEnvs.slice(0, -1).join(', ') + ' & ' + includedEnvs[includedEnvs.length - 1]
                : includedEnvs[0] ?? '';
              const allOpts = addonId ? (powerAddonOptions[addonId] ?? []) : [];
              const availableOpts: SelectOption[] = allOpts
                .filter((opt) => powerAddonOptionPricing[`${opt.PowerAddonOptionId}-${selectedRegion}`]?.IsAvailable)
                .map((opt) => ({ value: opt.PowerAddonOptionId, label: opt.OptionName }));
              const optLabel = (val: number | string) => allOpts.find((o) => o.PowerAddonOptionId === Number(val))?.OptionName ?? String(val);
              const optionOptsWithCurrent = ensureCurrentOption(availableOpts, item.powerAddonOptionId, optLabel);
              const selectedOption = item.powerAddonOptionId ? allOpts.find((o) => o.PowerAddonOptionId === Number(item.powerAddonOptionId)) : null;
              const selectedAddon = addonId ? powerAddons.find((a) => a.PowerAddonId === addonId) : null;
              const description = selectedOption?.Description || selectedAddon?.Description || '';

              return (
                <TableRow key={item.id} hover sx={isOneTime ? { borderLeft: '3px solid', borderColor: 'info.main', bgcolor: 'rgba(2, 136, 209, 0.04)' } : !available ? rowErrorSX : undefined}>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                    <TextField select size="small" value={item.powerAddonId}
                      onChange={(e) => onUpdatePowerAddon(item.id, 'powerAddonId', e.target.value)}
                      disabled={isOneTime}
                      fullWidth>
                      {addonOptsWithCurrent.map((opt) => (
                        <MenuItem key={`pa-${opt.value}`} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS2) }}>
                    {addonId && allOpts.length > 0 ? (
                      <TextField select size="small" value={item.powerAddonOptionId ?? ''} onChange={(e) => onUpdatePowerAddon(item.id, 'powerAddonOptionId', e.target.value)} disabled={isOneTime} fullWidth>
                        {optionOptsWithCurrent.map((opt) => <MenuItem key={`paopt-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ ...SPACER_CELL_SX, verticalAlign: 'middle', px: 1 }}>
                    {includedEnvs.length > 0 ? (
                      <Typography variant="body2" color="error" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        * This addon is already included in the price for {includedEnvsLabel} VMs.
                      </Typography>
                    ) : description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right' }}>
                        {description}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.QTY), textAlign: 'center' }}>
                    <TextField type="number" size="small" value={isOneTime ? 1 : item.quantity}
                      onChange={(e) => onUpdatePowerAddon(item.id, 'quantity', e.target.value)}
                      disabled={isOneTime}
                      inputProps={{ min: 1, step: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {available ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}>
                        <span>{formatCurrency(cost)}</span>
                        {isOneTime && <Typography component="span" variant="caption" sx={{ fontSize: '0.65rem', color: 'warning.dark', fontWeight: 600, lineHeight: 1 }}>one-time</Typography>}
                      </Box>
                    ) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.ACT), textAlign: 'right' }}>
                    {isOneTime ? (
                      <Tooltip title="One-time build fee — cannot be removed or changed">
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <LockIcon sx={{ fontSize: '1.1rem', color: 'info.main', opacity: 0.7 }} />
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" color="error" onClick={() => onDeletePowerAddon(item.id)}><DeleteIcon /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PowerAddonsTable;
